import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Send, RefreshCw, ToggleLeft, ToggleRight,
  CheckCircle, XCircle, Clock, AlertTriangle, Eye,
  RotateCcw, Settings, List, TestTube, Shield, ChevronDown, X,
  ShoppingCart, Play, Users, Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  submitted: { label: "Submitted", color: "#0369a1", bg: "#eff6ff" },
  sending: { label: "Sending…", color: "#7c3aed", bg: "#f5f3ff" },
  failed: { label: "Failed", color: "#dc2626", bg: "#fef2f2" },
  skipped: { label: "Skipped", color: "#64748b", bg: "#f8fafc" },
  delivered: { label: "Delivered", color: "#16a34a", bg: "#f0fdf4" },
  opened: { label: "Opened", color: "#16a34a", bg: "#f0fdf4" },
  clicked: { label: "Clicked", color: "#f97316", bg: "#fff7ed" },
  bounced: { label: "Bounced", color: "#dc2626", bg: "#fef2f2" },
  spam: { label: "Spam", color: "#dc2626", bg: "#fef2f2" },
};

const EMAIL_KIND_LABEL = {
  customer_registration: "Customer Registration",
  order_placed: "Order Placed",
  payment_success: "Payment Successful",
  payment_failed: "Payment Failed",
  order_accepted: "Order Accepted",
  order_rejected: "Order Rejected",
  order_packed: "Order Packed",
  order_shipped: "Order Shipped",
  out_for_delivery: "Out for Delivery",
  order_delivered: "Order Delivered",
  order_cancelled: "Order Cancelled",
  refund_initiated: "Refund Initiated",
  refund_completed: "Refund Completed",
  subscription_approved: "Subscription Approved",
  subscription_rejected: "Subscription Rejected",
  abandoned_cart: "Abandoned Cart Recovery",
  password_reset: "Password Reset",
  account_verification: "Account Verification",
};

const CATEGORY_ORDER = ["Customer", "Orders", "Payments", "Fulfillment", "Subscriptions", "Marketing", "Account"];

function StatusBadge({ status, lastEvent }) {
  const display = lastEvent || status || "unknown";
  const cfg = STATUS_BADGE[display] || { label: display, color: "#64748b", bg: "#f8fafc" };
  return (
    <span style={{ background: cfg.bg, color: cfg.color }}
      className="text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
      {cfg.label}
    </span>
  );
}

function PreviewModal({ kind, html, onClose }) {
  if (!html) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 flex-shrink-0">
          <div>
            <p className="text-sm font-bold text-slate-900">{EMAIL_KIND_LABEL[kind] || kind}</p>
            <p className="text-xs text-slate-500 mt-0.5">Email Preview</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <iframe
            data-testid="email-preview-iframe"
            srcDoc={html}
            title="Email Preview"
            className="w-full rounded-lg border border-slate-200"
            style={{ minHeight: 520 }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Tab: Templates ─────────────────────────────────────────────────────────
function TemplatesTab({ token }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewKind, setPreviewKind] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/email-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
      setDirty(false);
    } catch {
      toast.error("Failed to load email settings");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggleType = (kind) => {
    setSettings(prev => ({
      ...prev,
      email_types: {
        ...prev.email_types,
        [kind]: {
          ...prev.email_types[kind],
          enabled: !prev.email_types[kind].enabled
        }
      }
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const enabledTypes = {};
    Object.entries(settings.email_types || {}).forEach(([k, v]) => {
      enabledTypes[k] = v.enabled;
    });
    try {
      await axios.put(`${API}/admin/email-settings`,
        { enabled_types: enabledTypes, sender_name: settings.sender_name_override || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email settings saved");
      setDirty(false);
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async (kind) => {
    setPreviewLoading(true);
    setPreviewKind(kind);
    try {
      const res = await axios.get(`${API}/admin/email-preview/${kind}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(res.data.html);
    } catch {
      toast.error("Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
      </div>
    );
  }

  const emailTypes = settings?.email_types || {};

  // Group by category
  const grouped = {};
  Object.entries(emailTypes).forEach(([kind, meta]) => {
    const cat = meta.category || "Other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ kind, ...meta });
  });

  const configOk = settings?.brevo_configured && settings?.sender_email_configured;

  return (
    <div>
      {/* Config Warning */}
      {!configOk && (
        <div className="mb-5 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Brevo credentials not configured</p>
            <p className="mt-0.5 text-amber-700 text-xs">
              Add <code className="font-mono bg-amber-100 px-1 rounded">BREVO_API_KEY</code> and{" "}
              <code className="font-mono bg-amber-100 px-1 rounded">BREVO_SENDER_EMAIL</code> in{" "}
              Emergent Secrets to enable email sending.
            </p>
          </div>
        </div>
      )}

      {/* Sender Info */}
      {configOk && (
        <div className="mb-5 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          <div className="text-sm text-green-800">
            <span className="font-semibold">Brevo connected</span>
            <span className="text-green-700 text-xs ml-2">
              Sending from <span className="font-mono font-medium">{settings.env_sender_email}</span>
            </span>
          </div>
        </div>
      )}

      {/* Email Type Groups */}
      {CATEGORY_ORDER.map(cat => {
        const items = grouped[cat];
        if (!items || items.length === 0) return null;
        return (
          <div key={cat} className="mb-4">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">{cat}</p>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {items.map((item, idx) => (
                <div key={item.kind}
                  data-testid={`email-type-row-${item.kind}`}
                  className={`flex items-center gap-4 px-5 py-3.5 ${idx < items.length - 1 ? "border-b border-slate-100" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{item.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                  </div>
                  <button
                    data-testid={`email-preview-btn-${item.kind}`}
                    onClick={() => openPreview(item.kind)}
                    disabled={previewLoading}
                    title="Preview template"
                    className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`email-toggle-${item.kind}`}
                    onClick={() => toggleType(item.kind)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border flex-shrink-0 ${
                      item.enabled
                        ? "bg-orange-500 text-white border-orange-500 hover:bg-orange-600"
                        : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    {item.enabled
                      ? <><ToggleRight className="w-3.5 h-3.5" /> On</>
                      : <><ToggleLeft className="w-3.5 h-3.5" /> Off</>}
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Save */}
      <div className="flex items-center gap-3 mt-5">
        <button
          data-testid="email-settings-save-btn"
          onClick={handleSave}
          disabled={!dirty || saving}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            dirty ? "bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          {saving
            ? <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            : <Settings className="w-4 h-4" />}
          {saving ? "Saving…" : "Save Settings"}
        </button>
        {dirty && <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>}
      </div>

      {preview && (
        <PreviewModal kind={previewKind} html={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}


// ── Tab: Logs ──────────────────────────────────────────────────────────────
function LogsTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.append("status", filter);
      if (kindFilter) params.append("kind", kindFilter);
      params.append("limit", "100");
      const res = await axios.get(`${API}/admin/email-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data);
    } catch {
      toast.error("Failed to load email logs");
    } finally {
      setLoading(false);
    }
  }, [token, filter, kindFilter]);

  useEffect(() => { load(); }, [load]);

  const handleRetry = async (logId) => {
    setRetrying(logId);
    try {
      await axios.post(`${API}/admin/email-retry/${logId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email retry submitted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
      });
    } catch { return iso; }
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select
          data-testid="email-logs-status-filter"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 bg-white text-slate-700"
        >
          <option value="">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="sending">Sending</option>
          <option value="failed">Failed</option>
          <option value="delivered">Delivered</option>
        </select>
        <select
          data-testid="email-logs-kind-filter"
          value={kindFilter}
          onChange={e => setKindFilter(e.target.value)}
          className="text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 bg-white text-slate-700"
        >
          <option value="">All Types</option>
          {Object.entries(EMAIL_KIND_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <button onClick={load}
          className="text-slate-400 hover:text-slate-700 transition-colors ml-auto">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Mail className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p className="text-sm font-medium">No email logs yet</p>
          <p className="text-xs text-slate-400 mt-1">Sent emails will appear here</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-logs-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Type</th>
                  <th className="text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Recipient</th>
                  <th className="text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Sent At</th>
                  <th className="text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <tr key={log.id}
                    data-testid={`email-log-row-${idx}`}
                    className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${idx === logs.length - 1 ? "border-b-0" : ""}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-800 text-xs">{EMAIL_KIND_LABEL[log.kind] || log.kind}</p>
                      {log.order_id && <p className="text-[11px] text-slate-400 mt-0.5">#{log.order_id}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-slate-700">{log.recipient_email || "—"}</p>
                      {log.recipient_name && log.recipient_name !== log.recipient_email && (
                        <p className="text-[11px] text-slate-400 mt-0.5">{log.recipient_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={log.status} lastEvent={log.last_event} />
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{fmt(log.created_at)}</td>
                    <td className="px-4 py-3">
                      {log.status === "failed" && (
                        <button
                          data-testid={`email-retry-btn-${log.id}`}
                          onClick={() => handleRetry(log.id)}
                          disabled={retrying === log.id}
                          className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-semibold transition-colors disabled:opacity-50"
                        >
                          {retrying === log.id
                            ? <div className="w-3 h-3 rounded-full border border-orange-400 border-t-transparent animate-spin" />
                            : <RotateCcw className="w-3 h-3" />}
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Tab: Test Email ────────────────────────────────────────────────────────
function TestEmailTab({ token }) {
  const [form, setForm] = useState({ kind: "order_placed", recipient_email: "", recipient_name: "" });
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleSend = async () => {
    if (!form.recipient_email) {
      toast.error("Please enter recipient email");
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API}/admin/email-test`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Test email sent!");
      setLastResult({ ok: true, data: res.data });
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to send test email";
      toast.error(msg);
      setLastResult({ ok: false, error: msg });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-md">
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Send Test Email</p>
          <p className="text-[11px] text-slate-400 mt-0.5">Sends a real email with sample data</p>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email Type</label>
            <select
              data-testid="test-email-kind-select"
              value={form.kind}
              onChange={e => setForm(p => ({ ...p, kind: e.target.value }))}
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2.5 focus:outline-none focus:border-orange-500 bg-white text-slate-800"
            >
              {Object.entries(EMAIL_KIND_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Recipient Email</label>
            <input
              data-testid="test-email-recipient-input"
              type="email"
              value={form.recipient_email}
              onChange={e => setForm(p => ({ ...p, recipient_email: e.target.value }))}
              placeholder="test@example.com"
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2.5 focus:outline-none focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Recipient Name <span className="text-slate-400 font-normal">(optional)</span></label>
            <input
              data-testid="test-email-name-input"
              type="text"
              value={form.recipient_name}
              onChange={e => setForm(p => ({ ...p, recipient_name: e.target.value }))}
              placeholder="Priya Sharma"
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2.5 focus:outline-none focus:border-orange-500"
            />
          </div>
          <button
            data-testid="test-email-send-btn"
            onClick={handleSend}
            disabled={sending || !form.recipient_email}
            className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-bold py-2.5 rounded-lg transition-all"
          >
            {sending
              ? <><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Sending…</>
              : <><Send className="w-4 h-4" /> Send Test Email</>}
          </button>
        </div>
      </div>

      {lastResult && (
        <div className={`mt-4 flex items-start gap-3 rounded-xl p-4 border ${
          lastResult.ok ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
        }`}>
          {lastResult.ok
            ? <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
          <div className="text-sm">
            <p className={`font-semibold ${lastResult.ok ? "text-green-800" : "text-red-700"}`}>
              {lastResult.ok ? "Email submitted to Brevo" : "Failed to send"}
            </p>
            {lastResult.ok && lastResult.data?.result?.message_id && (
              <p className="text-xs text-green-700 mt-0.5 font-mono">
                Message ID: {lastResult.data.result.message_id}
              </p>
            )}
            {!lastResult.ok && (
              <p className="text-xs text-red-600 mt-0.5">{lastResult.error}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Tab: Sender Config ─────────────────────────────────────────────────────
function SenderConfigTab({ token }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/email-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch {
      toast.error("Failed to load config");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-4">
      {/* API Key Status */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" /> API Credentials
          </p>
        </div>
        <div className="px-5 divide-y divide-slate-100">
          <div className="py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-slate-800">BREVO_API_KEY</p>
              <p className="text-xs text-slate-500 mt-0.5">Stored securely in environment secrets</p>
            </div>
            <div className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${
              settings?.brevo_configured ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
            }`}>
              {settings?.brevo_configured
                ? <><CheckCircle className="w-3 h-3" /> Configured</>
                : <><XCircle className="w-3 h-3" /> Not Set</>}
            </div>
          </div>
          <div className="py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-slate-800">BREVO_SENDER_EMAIL</p>
              {settings?.env_sender_email && (
                <p className="text-xs text-slate-500 mt-0.5 font-mono">{settings.env_sender_email}</p>
              )}
            </div>
            <div className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${
              settings?.sender_email_configured ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
            }`}>
              {settings?.sender_email_configured
                ? <><CheckCircle className="w-3 h-3" /> Configured</>
                : <><XCircle className="w-3 h-3" /> Not Set</>}
            </div>
          </div>
          <div className="py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-slate-800">BREVO_SENDER_NAME</p>
              <p className="text-xs text-slate-500 mt-0.5 font-mono">{settings?.env_sender_name || "IMPORTREND"}</p>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3" /> Configured
            </div>
          </div>
        </div>
      </div>

      {/* How to configure */}
      {(!settings?.brevo_configured || !settings?.sender_email_configured) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm font-bold text-amber-900 mb-2">How to add Brevo credentials</p>
          <ol className="text-xs text-amber-800 space-y-1.5 list-decimal pl-4">
            <li>Go to <strong>Brevo → Settings → API Keys</strong> and create a new key</li>
            <li>Verify your sender email in <strong>Brevo → Senders & IP → Senders</strong></li>
            <li>In Emergent, open <strong>Secrets</strong> and add:</li>
          </ol>
          <div className="mt-2 bg-amber-100 rounded-lg p-3 font-mono text-xs text-amber-900 space-y-1">
            <p>BREVO_API_KEY = xkeysib-...</p>
            <p>BREVO_SENDER_EMAIL = no-reply@yourdomain.com</p>
            <p>BREVO_SENDER_NAME = IMPORTREND</p>
            <p>BREVO_WEBHOOK_SECRET = (optional, for delivery tracking)</p>
          </div>
        </div>
      )}

      {/* Webhook info */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Delivery Status Tracking</p>
        </div>
        <div className="px-5 py-4 text-sm text-slate-600">
          <p>Configure this webhook URL in <strong>Brevo → Webhooks → Transactional</strong>:</p>
          <div className="mt-2 bg-slate-50 rounded-lg px-3 py-2 font-mono text-xs text-slate-800 border border-slate-200 break-all">
            {process.env.REACT_APP_BACKEND_URL}/api/brevo/webhook
          </div>
          <p className="mt-2 text-xs text-slate-500">
            This receives delivery, bounce, open, and click events from Brevo.
          </p>
        </div>
      </div>
    </div>
  );
}


// ── Tab: Abandoned Cart ──────────────────────────────────────────────────────
function AbandonedCartTab({ token }) {
  const DEFAULT = { enabled: true, delay_hours: 1, reminder_hours: 24, max_reminders: 2 };
  const [settings, setSettings] = useState(null);
  const [carts, setCarts]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/abandoned-cart-settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/abandoned-carts`,         { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSettings({ ...DEFAULT, ...sRes.data });
      setCarts(cRes.data);
    } catch { toast.error("Failed to load abandoned cart data"); }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/abandoned-cart-settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Abandoned cart settings saved");
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const triggerNow = async () => {
    setTriggering(true);
    try {
      await axios.post(`${API}/admin/run-abandoned-cart`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Recovery emails queued — check Delivery Logs in a moment");
      setTimeout(load, 2500);
    } catch { toast.error("Failed to trigger — check backend logs"); }
    finally { setTriggering(false); }
  };

  const u = patch => setSettings(s => ({ ...s, ...patch }));

  const fmtDate = iso => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  if (loading || !settings) return <div className="flex items-center justify-center py-16"><RefreshCw className="w-5 h-5 text-orange-500 animate-spin" /></div>;

  const totalEmailsSent = carts.reduce((s, c) => s + (c.reminders_sent || 0), 0);

  return (
    <div className="space-y-5 max-w-3xl">
      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Abandoned Carts",    value: carts.length,        icon: ShoppingCart, color: "bg-orange-100 text-orange-600" },
          { label: "Recovery Emails Sent", value: totalEmailsSent,   icon: Mail,         color: "bg-blue-100 text-blue-600" },
          { label: "Items Waiting",        value: carts.reduce((s,c)=>s+(c.items?.length||0),0), icon: Users, color: "bg-green-100 text-green-600" },
        ].map(k => (
          <div key={k.label} className="bg-white border border-slate-100 rounded-xl p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${k.color}`}>
              <k.icon className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">{k.value}</p>
              <p className="text-xs text-slate-400">{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Settings card */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Recovery Email Settings</p>
          <div className="flex items-center gap-2">
            <button onClick={triggerNow} disabled={triggering}
              data-testid="trigger-abandoned-cart-btn"
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 hover:border-orange-400 hover:text-orange-600 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
              <Play className={`w-3 h-3 ${triggering ? "animate-pulse" : ""}`} />
              {triggering ? "Running…" : "Run Now"}
            </button>
            <button onClick={save} disabled={saving}
              className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
              {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Save
            </button>
          </div>
        </div>
        <div className="px-5 py-4 space-y-5">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800">Enable Abandoned Cart Emails</p>
              <p className="text-xs text-slate-500 mt-0.5">Automatically email customers who leave items without checking out</p>
            </div>
            <button onClick={() => u({ enabled: !settings.enabled })} data-testid="abandoned-cart-toggle"
              className={`relative w-11 h-6 rounded-full transition-colors ${settings.enabled ? "bg-orange-500" : "bg-slate-200"}`}>
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${settings.enabled ? "left-6" : "left-1"}`} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* First email delay */}
            <div>
              <p className="text-xs font-bold text-slate-600 mb-2">First Email After</p>
              <div className="flex flex-wrap gap-1.5">
                {[1,2,4,6,12].map(h => (
                  <button key={h} onClick={() => u({ delay_hours: h })}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${settings.delay_hours===h ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {h}h
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">{settings.delay_hours}h after abandonment</p>
            </div>

            {/* Second email */}
            <div>
              <p className="text-xs font-bold text-slate-600 mb-2">Second Email After</p>
              <div className="flex flex-wrap gap-1.5">
                {[12,24,48,72].map(h => (
                  <button key={h} onClick={() => u({ reminder_hours: h })}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${settings.reminder_hours===h ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {h}h
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">{settings.reminder_hours}h after first email</p>
            </div>

            {/* Max emails */}
            <div>
              <p className="text-xs font-bold text-slate-600 mb-2">Max Emails per Cart</p>
              <div className="flex gap-1.5">
                {[1,2,3].map(n => (
                  <button key={n} onClick={() => u({ max_reminders: n })}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${settings.max_reminders===n ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Stop after {settings.max_reminders} email{settings.max_reminders!==1?"s":""}</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800">
            <strong>How it works:</strong> When a logged-in customer adds items to cart and doesn't place an order, their cart is synced to the server. The cron runs hourly and sends recovery emails after the configured delay. Use <strong>Run Now</strong> to test immediately.
          </div>
        </div>
      </div>

      {/* Abandoned carts table */}
      {carts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Abandoned Carts ({carts.length})</p>
          </div>
          <div className="grid grid-cols-12 gap-2 px-5 py-2 text-[11px] font-bold text-slate-400 uppercase bg-slate-50/50">
            <span className="col-span-4">Customer</span>
            <span className="col-span-2">Items</span>
            <span className="col-span-3">Abandoned At</span>
            <span className="col-span-2">Emails Sent</span>
            <span className="col-span-1">Last</span>
          </div>
          {carts.map((c, i) => (
            <div key={c.id||i} className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-t border-slate-50 hover:bg-slate-50/50">
              <div className="col-span-4 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{c.customer_name||"—"}</p>
                <p className="text-xs text-slate-400 truncate">{c.customer_email}</p>
              </div>
              <div className="col-span-2 flex gap-1">
                {(c.items||[]).slice(0,2).map((it,j)=>it.image
                  ? <img key={j} src={it.image} alt="" className="w-8 h-8 rounded object-cover"/>
                  : <div key={j} className="w-8 h-8 bg-slate-100 rounded text-[10px] text-slate-400 flex items-center justify-center">{j+1}</div>
                )}
                {(c.items?.length||0)>2 && <span className="text-xs text-slate-400 self-center">+{c.items.length-2}</span>}
              </div>
              <p className="col-span-3 text-xs text-slate-500">{fmtDate(c.updated_at)}</p>
              <p className="col-span-2 text-xs font-bold text-orange-500">{c.reminders_sent||0}</p>
              <p className="col-span-1 text-xs text-slate-400">{fmtDate(c.last_reminder_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Main Page ──────────────────────────────────────────────────────────────
export default function EmailNotifications() {
  const [activeTab, setActiveTab] = useState("templates");
  const token = localStorage.getItem("it_token");

  const tabs = [
    { id: "templates",      label: "Templates",      icon: Settings },
    { id: "abandoned_cart", label: "Abandoned Cart",  icon: ShoppingCart },
    { id: "logs",           label: "Delivery Logs",   icon: List },
    { id: "test",           label: "Test Email",      icon: TestTube },
    { id: "config",         label: "Sender Config",   icon: Shield },
  ];

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
          <Mail className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-slate-900">Email Notifications</h1>
          <p className="text-xs text-slate-500">Manage transactional emails via Brevo</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 mb-6 w-fit">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`email-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "templates"      && <TemplatesTab token={token} />}
      {activeTab === "abandoned_cart" && <AbandonedCartTab token={token} />}
      {activeTab === "logs"           && <LogsTab token={token} />}
      {activeTab === "test"           && <TestEmailTab token={token} />}
      {activeTab === "config"         && <SenderConfigTab token={token} />}
    </div>
  );
}
